'use client';

import { useEffect, useState } from 'react';
import { api, getApiErrorMessage } from '@/lib/api';
import { operationalPhaseFromEvent, type DockOperationalPhase } from '@/lib/dock-status';
import type { DockBay, DockDetail, DockEventPayload } from '@/types/dock';

// Both hooks stamp the dock id onto their state and only surface it for a
// matching id. That way switching docks can never briefly show the previous
// dock's bays or phase, without a reset render pass.

interface BayState {
  dockId: string;
  bays: DockBay[];
  error: string | null;
}

/**
 * Bay roster for the selected dock (`GET /api/docks/:id`).
 *
 * Bay assignment lives on the drone (drones.dock_id + bay_index, migration 015)
 * and changes only when an operator re-assigns an airframe, so it is fetched on
 * selection rather than streamed.
 */
export function useDockBays(dockId: string | null): { bays: DockBay[]; error: string | null } {
  const [state, setState] = useState<BayState | null>(null);

  useEffect(() => {
    if (dockId === null) return;

    let cancelled = false;
    api
      .get<{ data: DockDetail }>(`/api/docks/${dockId}`)
      .then((res) => {
        if (!cancelled) setState({ dockId, bays: res.data.bays ?? [], error: null });
      })
      .catch((err: unknown) => {
        // Surface it — an empty bay list must never be mistaken for "no drones".
        if (!cancelled) {
          setState({ dockId, bays: [], error: getApiErrorMessage(err, 'Failed to load dock bays') });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [dockId]);

  if (dockId === null || state?.dockId !== dockId) return { bays: [], error: null };
  return { bays: state.bays, error: state.error };
}

/**
 * Latest operational phase reported by the dock.
 *
 * Driven purely by AirScort LifecycleEvents on the dock WS channel. The Pi does
 * not publish them yet (docs/airscort-api-spec.md open question §5), so this
 * stays null and the sidebar shows "AWAITING DATA" — no phase is ever inferred
 * from dock status or from commands SHOVRON itself issued.
 */
export function useDockOperationalPhase(
  dockId: string | null,
  subscribeDockEvent: (cb: (event: DockEventPayload) => void) => () => void,
): DockOperationalPhase | null {
  const [state, setState] = useState<{ dockId: string; phase: DockOperationalPhase } | null>(null);

  useEffect(() => {
    if (dockId === null) return;
    return subscribeDockEvent((event) => {
      if (event.dock_id !== dockId) return;
      const phase = operationalPhaseFromEvent(event.phase);
      if (phase !== null) setState({ dockId, phase });
    });
  }, [dockId, subscribeDockEvent]);

  return state?.dockId === dockId ? state.phase : null;
}
